import { createFileRoute } from "@tanstack/react-router";
import { CalendarPage } from "../components/calendar/calendar-page.tsx";

/**
 * The calendar is the app's front door.
 *
 * BUILD.md §1.2 lists three things this SPA eventually holds — the staff calendar, franchise
 * admin (P9) and the HQ dashboard (P10) — and a technician opening it wants exactly one of
 * them. Landing anywhere else would be a click between them and their day.
 *
 * ── Why the date is in the URL ────────────────────────────────────────────────
 *
 * Because a calendar whose position lives in component state loses it on every reload, and
 * "where am I" is the thing DESIGN.md §10 is most protective of. In the URL it survives a
 * refresh, it goes in the back button, and a franchise owner can send someone a link to the
 * Thursday they are arguing about. It is also what makes the day view addressable from an
 * email or a notification later.
 *
 * `view` rides along for the same reason. It is *also* persisted per user (`lib/prefs.ts`),
 * and the two do not conflict: the URL wins when it says something, the preference supplies
 * the default when it does not. Someone who lives in the week view gets it on open; someone
 * following a link to a specific day gets that day.
 */

export interface CalendarSearch {
  /** `2026-08-24`. Absent means today, in the franchise's zone. */
  readonly date?: string;
  readonly view?: "day" | "week" | "month";
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/")({
  /**
   * Search params are user input. A hand-edited `?date=lol` must land on today rather than
   * throwing a `RangeError` out of Temporal and blanking the page — so anything unparseable
   * is dropped here and the component sees an absent value, which it already handles.
   */
  validateSearch: (search: Record<string, unknown>): CalendarSearch => {
    const date = search.date;
    const view = search.view;

    return {
      ...(typeof date === "string" && ISO_DATE.test(date) ? { date } : {}),
      ...(view === "day" || view === "week" || view === "month" ? { view } : {}),
    };
  },
  component: CalendarPage,
});
